import React, { useState, useEffect } from 'react';
import styles from './home.module.css';
import apiClient from '../../util/api-client';

export const DEFAULT_COMPETITIONS = [
  { id: 'comp-1', name: 'Fotboll', type: 'quantity', points: 10 },
  { id: 'comp-2', name: 'Löpning', type: 'time', points: 5 },
  { id: 'comp-3', name: 'Basket', type: 'quantity', points: 10 },
  { id: 'comp-4', name: 'Dodgeball', type: 'quantity', points: 15 },
  { id: 'comp-5', name: 'Stafett', type: 'time', points: 20 },
];

function CompetitionGrid({ classId, setClassTotals, competitions, canEdit, onAddCompetition }) {
  const [competitionData, setCompetitionData] = useState([]);
  const [newCompName, setNewCompName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const effectiveCompetitions =
    competitions && Array.isArray(competitions)
      ? competitions
      : [];

  // Ladda sparad poäng och namn data från MongoDB och localStorage
  useEffect(() => {
    const applyScores = (parsedData) => {
      if (parsedData && Array.isArray(parsedData) && parsedData.length > 0) {
        setCompetitionData(
          effectiveCompetitions.map((competition) => {
            const savedCompetition = parsedData.find((saved) => saved.name === competition.name);
            return {
              ...competition,
              id: competition.id || competition._id || `${competition.name}-${Date.now()}`,
              fields: savedCompetition?.fields || [{ value: '', name: '' }],
            };
          })
        );
      } else {
        setCompetitionData(
          effectiveCompetitions.map((competition) => ({
            ...competition,
            id: competition.id || competition._id || `${competition.name}-${Date.now()}`,
            fields: [{ value: '', name: '' }],
          }))
        );
      }
    };

    try {
      const savedData = localStorage.getItem(`competitionScores-${classId}`);
      if (savedData) {
        applyScores(JSON.parse(savedData));
      } else {
        applyScores([]);
      }
    } catch (e) {
      applyScores([]);
    }

    // Fetch live scores from MongoDB
    if (classId) {
      apiClient.getCompetitionScores(classId).then((remoteData) => {
        if (remoteData && remoteData.length > 0) {
          applyScores(remoteData);
        }
      });
    }
  }, [classId, competitions]);

  // Hantera ändringar av poäng och namn
  const handleValueChange = (rowIndex, colIndex, fieldName, value) => {
    const updatedData = competitionData.map((row, rIndex) => {
      if (rIndex === rowIndex) {
        const updatedFields = row.fields.map((field, fIndex) => {
          if (fIndex === colIndex) {
            return { ...field, [fieldName]: value };
          }
          return field;
        });
        return { ...row, fields: updatedFields };
      }
      return row;
    });

    setCompetitionData(updatedData);

    // Spara poäng och namn data till MongoDB och localStorage
    if (classId) {
      const dataToSave = updatedData.map((row) => ({
        name: row.name,
        fields: row.fields,
      }));
      apiClient.saveCompetitionScores(classId, dataToSave);
    }
  };

  // Beräkna totals för varje tävling och uppdatera classTotals
  useEffect(() => {
    if (competitionData.length > 0 && setClassTotals) {
      const totals = competitionData.reduce((acc, row) => {
        row.fields.forEach((field, index) => {
          let calculatedValue = parseInt(field.value, 10) || 0;
          acc[index] = (acc[index] || 0) + calculatedValue;
        });
        return acc;
      }, []);
      setClassTotals(totals);
    }
  }, [competitionData, classId, setClassTotals]);

  // Uppdatera klassens totala poäng i databasen och localStorage
  useEffect(() => {
    if (competitionData.length > 0 && classId) {
      const total = competitionData.reduce((total, row) => {
        return (
          total +
          row.fields.reduce((rowSum, field) => {
            return rowSum + (parseInt(field.value, 10) || 0);
          }, 0)
        );
      }, 0);

      const classes = JSON.parse(localStorage.getItem('classes') || '[]');
      let hasChanges = false;
      const updatedClasses = classes.map((cls) => {
        const cId = cls.id || cls._id;
        if (cId === classId || cls.name === classId || cls._id === classId) {
          hasChanges = true;
          return { ...cls, score: total };
        }
        return cls;
      });

      if (hasChanges) {
        apiClient.saveClasses(updatedClasses);
      }
    }
  }, [competitionData, classId]);

  // Hantera Enter-knappen för att multiplicera värde och poäng
  const handleEnterPress = (rowIndex, colIndex, fieldName, value, type) => {
    if (fieldName === 'value' || fieldName === 'points') {
      const updatedValue = parseInt(value, 10) || 0;
      const compPoints = parseInt(competitionData[rowIndex].points, 10) || 0;

      if ((type === 'time' || type === 'quantity') && fieldName === 'value' && compPoints > 0) {
        handleValueChange(rowIndex, colIndex, fieldName, updatedValue * compPoints);
      }
    }
  };

  const handleAddNewCompetition = () => {
    if (!newCompName.trim()) return;
    const newComp = {
      id: `comp-${Date.now()}`,
      name: newCompName.trim(),
      type: 'quantity',
      points: 10,
    };
    if (onAddCompetition) {
      onAddCompetition(newComp);
    } else {
      const updatedComps = [...effectiveCompetitions, newComp];
      apiClient.saveCompetitions(updatedComps);
    }
    setNewCompName('');
    setIsAdding(false);
  };

  // Räkna ihop totalsumman
  const totalSum = competitionData.reduce((total, row) => {
    return (
      total +
      row.fields.reduce((rowSum, field) => {
        return rowSum + (parseInt(field.value, 10) || 0);
      }, 0)
    );
  }, 0);

  return (
    <div className={styles.gridContainer}>
      {canEdit && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
          {isAdding ? (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Tävlingsnamn (t.ex. Hopprep, Plankan)..."
                value={newCompName}
                onChange={(e) => setNewCompName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddNewCompetition()}
                style={{
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  background: 'rgba(255, 255, 255, 0.08)',
                  color: '#ffffff',
                  outline: 'none',
                  fontSize: '0.9rem',
                }}
              />
              <button
                type="button"
                onClick={handleAddNewCompetition}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
              >
                Spara tävling
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsAdding(false);
                  setNewCompName('');
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                Avbryt
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                background: 'rgba(99, 102, 241, 0.25)',
                border: '1px solid rgba(99, 102, 241, 0.5)',
                color: '#ffffff',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
              }}
            >
              <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>+</span> Lägg till tävling
            </button>
          )}
        </div>
      )}

      <table className={styles.competitionTable}>
        <thead>
          <tr>
            <th>Tävlingar</th>
            {competitionData[0] && competitionData[0].fields.length > 0 ? (
              competitionData[0].fields.map((field, index) => (
                <React.Fragment key={`field-header-${index}`}>
                  <th>Poäng</th>
                  <th>Namn</th>
                </React.Fragment>
              ))
            ) : (
              <>
                <th>Poäng</th>
                <th>Namn</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {competitionData.map((row, rowIndex) => (
            <tr key={row.id || `${row.name}-${rowIndex}`}>
              <td>
                <span className={styles.competitionName1}>{row.name}</span>
              </td>
              {row.fields.map((field, colIndex) => (
                <React.Fragment key={`field-${rowIndex}-${colIndex}`}>
                  <td>
                    <input
                      type="text"
                      value={field.value}
                      placeholder={canEdit ? (row.type === 'time' ? 'Ex: 5 min' : 'Ex: 5 ringar') : '-'}
                      onChange={(e) => handleValueChange(rowIndex, colIndex, 'value', e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleEnterPress(rowIndex, colIndex, 'value', e.target.value, row.type);
                        }
                      }}
                      disabled={!canEdit}
                      className={!canEdit ? styles.disabledInput : ''}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={field.name}
                      placeholder={canEdit ? 'Namn' : '-'}
                      onChange={(e) => handleValueChange(rowIndex, colIndex, 'name', e.target.value)}
                      disabled={!canEdit}
                      className={!canEdit ? styles.disabledInput : ''}
                    />
                  </td>
                </React.Fragment>
              ))}
            </tr>
          ))}
          {/* Lägg till en rad för totalsumman under Tävlingar */}
          <tr className={styles.totalRow}>
            <td colSpan={((competitionData[0]?.fields.length) || 1) * 2 + 1}>
              <div className={styles.totalContainer}>
                <span className={styles.totalLabel}>Totalt:</span>
                <span className={styles.totalValue}>{totalSum}</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default CompetitionGrid;
